<template>
  <GameLoaderModal v-if="isGameLoaderModalOpen" />

  <MaintenanceModal
    v-else-if="isMaintenanceModalOpen"
    @redirect:homePage="handleRedirectToHomePageFromMaintenanceModal"
  />

  <ZeroBalanceGameModal
    v-if="isZeroBalanceGameModalOpen"
    @redirect:storePage="handleRedirectToStorePageFromZeroBalanceGameModal"
  />
</template>

<script setup>
import { onMounted, onUnmounted } from 'vue';
import { useRoute } from 'vue-router';
import GameLoaderModal from '@/components/modal/loader/GameLoaderModal.vue';
import MaintenanceModal from '@/components/modal/MaintenanceModal.vue';
import ZeroBalanceGameModal from '@/components/modal/game/ZeroBalanceGameModal.vue';
import useAuthStore from '@/store/modules/auth.js';
import useGameStore from '@/store/modules/game.js';
import useSocketHook from '@/hooks/useSocket.js';
import usePageNavigationHook from '@/hooks/usePageNavigation.js';
import useModalHook from '@/hooks/useModal.js';
import { apiGetNewGameStatus } from '@/services/game.js';
import { gameCategoryEnum, gameThemeEnum, preGameErrorReasonEnum } from '@/constants/game.js';
import { apiResponseTypeEnum } from '@/constants/api.js';

const emits = defineEmits(['hide:preGame']);

const authStore = useAuthStore();
const gameStore = useGameStore();

const route = useRoute();

const {
  createStompClient,
  connectStompClient,
  removeStompClient,
  subscribeToChannel,
  unsubscribeFromChannel,
} = useSocketHook(
  '/gameData',
  {
    fetchAllGameData: null,
  },
);

const { redirectTo, redirectToHomePage, redirectToStorePage } = usePageNavigationHook();
const [isGameLoaderModalOpen, openGameLoaderModal, closeGameLoaderModal] = useModalHook();
const [isMaintenanceModalOpen, openMaintenanceModal, closeMaintenanceModal] = useModalHook();
const [isZeroBalanceGameModalOpen, openZeroBalanceGameMdal, closeZeroBalanceGameMdal] = useModalHook();

const connectToSocket = () => {
  return new Promise((resolve) => {
    connectStompClient(resolve);
  });
};

const clearSocketAndCloseLoader = async () => {
  unsubscribeFromChannel('fetchAllGameData');

  await removeStompClient();

  await closeGameLoaderModal({ immediate: true });
};

const emitHidePreGame = ({ canProceed = false } = {}) => {
  emits('hide:preGame', { canProceed });
};

const hidePreGameWithHomeRedirection = (isRedirectionNeeded = true) => {
  emitHidePreGame();

  if (isRedirectionNeeded) {
    redirectToHomePage();
  }
};

const handleRedirectToHomePageFromMaintenanceModal = async () => {
  const isRedirectionNeeded = route.name !== 'Home';

  await closeMaintenanceModal({ immediate: isRedirectionNeeded });

  hidePreGameWithHomeRedirection(isRedirectionNeeded);
};

const handleRedirectToStorePageFromZeroBalanceGameModal = async () => {
  const isRedirectionNeeded = route.name !== 'Store';

  await closeZeroBalanceGameMdal({ immediate: isRedirectionNeeded });

  emitHidePreGame();

  if (isRedirectionNeeded) {
    redirectToStorePage();
  }
};

const handleError = (reason) => {
  gameStore.resetGameType();

  if (!Object.values(preGameErrorReasonEnum).includes(reason)) {
    emitHidePreGame();

    return;
  }

  const isRedirectionNeeded = !['Home', 'Store', 'ProfileOverview', 'Notifications'].includes(route.name);

  const afterHideCallback = () => {
    hidePreGameWithHomeRedirection(isRedirectionNeeded);
  };

  if (reason === preGameErrorReasonEnum.MAINTENANCE) {
    openMaintenanceModal({
      showImmediate: true,
      hideImmediate: isRedirectionNeeded,
      afterHideCallback,
    });
  } else if (reason === preGameErrorReasonEnum.ZERO_BALANCE) {
    openZeroBalanceGameMdal({
      showImmediate: true,
      hideImmediate: isRedirectionNeeded,
      afterHideCallback,
    });
  }
};

const subscribeToFetchAllGameDataChannel = () => {
  subscribeToChannel(
    'fetchAllGameData',
    `/topic/ctasFetched/${ authStore.user.msisdn }`,
    async (parsedData) => {
      await clearSocketAndCloseLoader();

      if ((parsedData === null) || (parsedData === 'Maintenance')) {
        handleError(preGameErrorReasonEnum.MAINTENANCE);
      } else {
        gameStore.setGameId(parsedData.id);

        gameStore.setGameRoundList(parsedData.miniGames.map((data) => ({
          ...data,
          __answerExtraInfo: { text: '', jokerUsedType: null },
        })));

        emitHidePreGame({ canProceed: true });

        if (route.name !== 'MainGame') {
          gameStore.isPreGameCompleted = true;

          redirectTo('MainGame', { replace: route.name === 'SelectCategoryGame' });
        }
      }
    },
    async () => {
      await clearSocketAndCloseLoader();

      handleError(preGameErrorReasonEnum.MAINTENANCE);
    });
};

const validateGameAccessibility = async () => {
  try {
    if (!Object.values({ ...gameThemeEnum, ...gameCategoryEnum }).includes(gameStore.gameType)) {
      return { error: true, reason: preGameErrorReasonEnum.MAINTENANCE };
    }

    await authStore.dispatchRetrieveAuthUserLoginDetails();

    const newGameStatusResponse = await apiGetNewGameStatus({
      queryParams: { gameTheme: gameStore.gameType },
    });

    if ([apiResponseTypeEnum.SERVER_ERROR, apiResponseTypeEnum.EVENT_LOCKED].includes(newGameStatusResponse.responseEnum)) {
      return { error: true, reason: preGameErrorReasonEnum.MAINTENANCE };
    } else if (newGameStatusResponse.responseEnum === apiResponseTypeEnum.NO_AVAILABLE_GAMES) {
      return { error: true, reason: preGameErrorReasonEnum.ZERO_BALANCE };
    }

    return { error: false, reason: null };
  } catch {
    return { error: true, reason: preGameErrorReasonEnum.MAINTENANCE };
  }
};

const checkRequirementsAndFetchGame = async () => {
  openGameLoaderModal({ hideManually: true });

  createStompClient();

  await connectToSocket();

  subscribeToFetchAllGameDataChannel();

  const response = await validateGameAccessibility();

  if (response.error) {
    await clearSocketAndCloseLoader();

    handleError(response.reason);
  }

  /**
   * Socket server will continue execution asynchronously via the subscribed channel (ref. 'subscribeToFetchAllGameDataChannel'),
   * except when 'validateGameAccessibility' returns an error response.
   */
};

onMounted(checkRequirementsAndFetchGame);

onUnmounted(clearSocketAndCloseLoader);
</script>
